import {
  ORGANIZATION_ID,
  PROJECT_ITEMS,
  SERVICE_ITEMS,
  SITE_URL,
  WEBSITE_ID,
  getCanonicalUrl,
  getLocalizedRouteSeo,
  normalizePathname,
  normalizeSeoLanguage,
} from './routes';

const organizationDescriptions = {
  pt: 'Tecnologia que conecta inovacao e impacto.',
  en: 'Technology that connects innovation and impact.',
  de: 'Technologie, die Innovation und Wirkung verbindet.',
};

const homeBreadcrumbs = {
  pt: 'Inicio',
  en: 'Home',
  de: 'Startseite',
};

const aboutFaqContent = {
  pt: [
    {
      question: 'Qual e o principal diferencial da BlueAlps?',
      answer:
        'Unimos qualidade suica, criatividade brasileira e uma operacao eficiente para entregar tecnologia de alto desempenho com um dos menores valores do mercado.',
    },
    {
      question: 'A BlueAlps atende empresas fora da Suica?',
      answer:
        'Sim. Atuamos com empresas na Suica, no Brasil e em outros mercados, com comunicacao remota, processos claros e entregas estruturadas.',
    },
    {
      question: 'Que tipo de solucao voces desenvolvem?',
      answer:
        'Desenvolvemos software personalizado, automacoes, solucoes com inteligencia artificial, integracoes, sites, sistemas internos e projetos de transformacao digital.',
    },
    {
      question: 'Como funciona o inicio de um projeto?',
      answer:
        'Comecamos entendendo o objetivo, o cenario atual e as prioridades do cliente. Depois propomos escopo, etapas, prazo e investimento de forma transparente.',
    },
  ],
  en: [
    {
      question: 'What is BlueAlps main differentiator?',
      answer:
        'We combine Swiss quality, Brazilian creativity, and an efficient operation to deliver high-performance technology with one of the most competitive rates in the market.',
    },
    {
      question: 'Does BlueAlps work with companies outside Switzerland?',
      answer:
        'Yes. We work with companies in Switzerland, Brazil, and other markets through remote communication, clear processes, and structured deliveries.',
    },
    {
      question: 'What type of solutions do you build?',
      answer:
        'We build custom software, automations, AI solutions, integrations, websites, internal systems, and digital transformation projects.',
    },
    {
      question: 'How does a project start?',
      answer:
        'We begin by understanding the goal, current context, and client priorities. Then we propose scope, steps, timeline, and investment transparently.',
    },
  ],
  de: [
    {
      question: 'Was ist das wichtigste Differenzierungsmerkmal von BlueAlps?',
      answer:
        'Wir verbinden Schweizer Qualitaet, brasilianische Kreativitaet und effiziente Ablaeufe, um leistungsstarke Technologie zu einem der wettbewerbsfaehigsten Preise am Markt zu liefern.',
    },
    {
      question: 'Arbeitet BlueAlps mit Unternehmen ausserhalb der Schweiz?',
      answer:
        'Ja. Wir arbeiten mit Unternehmen in der Schweiz, in Brasilien und in weiteren Maerkten mit Remote-Kommunikation, klaren Prozessen und strukturierten Lieferungen.',
    },
    {
      question: 'Welche Loesungen entwickelt ihr?',
      answer:
        'Wir entwickeln individuelle Software, Automatisierungen, KI-Loesungen, Integrationen, Websites, interne Systeme und Projekte zur digitalen Transformation.',
    },
    {
      question: 'Wie startet ein Projekt?',
      answer:
        'Wir beginnen damit, Ziel, aktuelle Situation und Prioritaeten des Kunden zu verstehen. Danach schlagen wir Umfang, Schritte, Zeitplan und Investition transparent vor.',
    },
  ],
};

const buildOrganization = (language?: string) => {
  const seoLanguage = normalizeSeoLanguage(language);

  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'BlueAlps',
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/#organization-logo`,
      url: `${SITE_URL}/logo.webp`,
      contentUrl: `${SITE_URL}/logo.webp`,
      caption: 'BlueAlps',
    },
    image: `${SITE_URL}/logo.webp`,
    description: organizationDescriptions[seoLanguage],
    areaServed: ['Switzerland', 'Brazil'],
    knowsAbout: [
      'Technical SEO',
      'PIM',
      'WordPress',
      'UX',
      'Cybersecurity',
      'Accessibility',
      'Sustainable technology',
    ],
    sameAs: [
      'https://www.linkedin.com/company/bluealps/',
      'https://www.instagram.com/bluealps_org/',
    ],
  };
};

const buildWebsite = () => ({
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: 'BlueAlps',
  publisher: {
    '@id': ORGANIZATION_ID,
  },
});

const buildBreadcrumb = (pathname: string, language?: string) => {
  const route = getLocalizedRouteSeo(pathname, language);
  const seoLanguage = normalizeSeoLanguage(language);
  const normalizedPath = normalizePathname(pathname);
  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: homeBreadcrumbs[seoLanguage],
      item: `${SITE_URL}/`,
    },
  ];

  if (normalizedPath !== '/') {
    itemListElement.push({
      '@type': 'ListItem',
      position: 2,
      name: route.breadcrumb,
      item: getCanonicalUrl(pathname),
    });
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${getCanonicalUrl(pathname)}#breadcrumb`,
    itemListElement,
  };
};

const buildWebPage = (pathname: string, language?: string) => {
  const route = getLocalizedRouteSeo(pathname, language);
  const type =
    route.type === 'AboutPage'
      ? ['AboutPage', 'WebPage']
      : (route.type ?? 'WebPage');

  return {
    '@type': type,
    '@id': `${getCanonicalUrl(pathname)}#webpage`,
    url: getCanonicalUrl(pathname),
    name: route.title,
    description: route.description,
    isPartOf: {
      '@id': WEBSITE_ID,
    },
    about: {
      '@id': ORGANIZATION_ID,
    },
    breadcrumb: {
      '@id': `${getCanonicalUrl(pathname)}#breadcrumb`,
    },
  };
};

const buildAboutFaqPage = (language?: string) => {
  const seoLanguage = normalizeSeoLanguage(language);

  return {
    '@type': 'FAQPage',
    '@id': `${SITE_URL}/about#faq`,
    url: `${SITE_URL}/about`,
    name: getLocalizedRouteSeo('/about', language).title,
    inLanguage: seoLanguage,
    isPartOf: {
      '@id': `${SITE_URL}/about#webpage`,
    },
    mainEntity: aboutFaqContent[seoLanguage].map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
};

const buildOfferCatalog = () => ({
  '@type': 'OfferCatalog',
  '@id': `${SITE_URL}/services#offer-catalog`,
  name: 'BlueAlps services',
  itemListElement: SERVICE_ITEMS.map((serviceName) => ({
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Service',
      name: serviceName,
      provider: {
        '@id': ORGANIZATION_ID,
      },
      areaServed: ['Switzerland', 'Brazil'],
    },
  })),
});

const buildProjectList = () => ({
  '@type': 'ItemList',
  '@id': `${SITE_URL}/projects#projects`,
  name: 'BlueAlps projects',
  itemListElement: PROJECT_ITEMS.map((projectName, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: projectName,
  })),
});

const buildContactPoint = () => ({
  '@type': 'ContactPoint',
  '@id': `${SITE_URL}/contact#contact-point`,
  contactType: 'customer support',
  email: 'contact@bluealps.org',
  telephone: '+41-71-841-12-16',
  areaServed: ['Switzerland', 'Brazil'],
  availableLanguage: ['English', 'German', 'Portuguese'],
});

export const buildStructuredData = (pathname: string, language?: string) => {
  const normalizedPath = normalizePathname(pathname);
  const graph: object[] = [
    buildOrganization(language),
    buildWebsite(),
    buildWebPage(pathname, language),
    buildBreadcrumb(pathname, language),
  ];

  if (normalizedPath === '/services') {
    graph.push(buildOfferCatalog());
  }

  if (normalizedPath === '/about') {
    graph.push(buildAboutFaqPage(language));
  }

  if (normalizedPath === '/projects') {
    graph.push(buildProjectList());
  }

  if (normalizedPath === '/contact') {
    graph.push(buildContactPoint());
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};
